/* eslint-disable @typescript-eslint/no-empty-interface,@typescript-eslint/no-misused-promises */
import React, { useRef, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { history, useSelector } from 'umi';
import { Layout, Icon, Button, Modal } from 'tea-component';
import RoutePrompt from '@gp/components/RoutePrompt';
import GameOverview from '@gp/pages/Workspace/Product/components/GameOverview';
import usePageLog from '@gp/hooks/usePageLog';
import { reportOperationLog } from '@gp/services/global';
import { goToProjectDetailsPage, notificationTips, messageTips } from '@gp/utils/common';
import { getParams } from './utils';
import { CONSTANT_TEXTS } from '@gp/constants/const';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import { MODAL_CFG_MAPPING, DEFAULT_INITIALPARAMS } from './constants';
import { useAutoSaveParams, EDIT_PAGE_KEY_MAP } from '@gp/hooks/useAutoSaveParams';
import AutoSaveModal from '@gp/components/AutoSaveModal';
import type { IBaseProps } from '@home/types/common';
import type { IModalType, ISubModuleInstance } from '@gp/pages/Workspace/Product/types';
import { updateProjectInformation } from '@gp/services/projectDetail';
import '@gp/assets/css/pages/product.css';
import './index.scss';

// 旧代码有引用，仅提供兼容
export interface AddNewOrEditProduct {
  currentPage: 'newAdded' | 'edit';
}

interface IAddNewProductProps extends IBaseProps {}

const { Content } = Layout;

const AddNewProduct: React.FC<IAddNewProductProps> = (props) => {
  const {} = props;

  // 页面打点
  usePageLog({ pageId: 'WORKSPACE_ADD_PRODUCT', uidType: '', uid: '' });
  const pageKeyCur = EDIT_PAGE_KEY_MAP.myWorkspace_addNewProduct;

  // 新添加的Project的id、name
  const project = useRef({
    project_id: '',
    project: '',
  });

  const { userInfo } = useSelector((state: any) => ({
    userInfo: state?.home?.userInfo,
  }));
  const { userid = '' } = userInfo || {};

  const hasChangedRef = useRef(false);

  // 是否提交中
  const [submitting, setSubmitting] = useState(false);
  // 控制对话框显示/隐藏
  const [visible, setVisible] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  // 对话框类型
  const [modalType, setModalType] = useState<IModalType>('none');
  // 对话框文案配置
  const modalCfg = MODAL_CFG_MAPPING[modalType];
  // GameOverview模块引用
  const gameOverview = useRef<ISubModuleInstance>(null);
  // GameOverview模块数据
  const [gameOverviewData, setGameOverviewData] = useState<Record<string, any> | null>(null);
  // GameOverview模块是否有填写数据
  const isGameOverviewAvailableData = useMemo<boolean>(() => {
    if (!gameOverviewData) return false;
    return Object.entries(gameOverviewData).some(([key, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      // if (key === 'group') return !!value?.parent || !!value?.child;
      // return !!value;
    });
  }, [gameOverviewData]);

  const [autoSaveVisible, setAutoSaveVisible] = useState(false);

  useEffect(() => {
    if (!isGameOverviewAvailableData && hasChangedRef.current && showRestorePrompt(pageKeyCur)) {
      deleteSaveParamsByPageKey(pageKeyCur);
    }
  }, [hasChangedRef.current, isGameOverviewAvailableData]);

  // 每隔5秒自动保存
  const { getSaveParamsValue, showRestorePrompt, deleteSaveParamsByPageKey } = useAutoSaveParams({
    pageKey: pageKeyCur,
    initialParams: DEFAULT_INITIALPARAMS,
    saveParamsCur: gameOverviewData,
    isBegin: !isSubmit,
    isChangeValue: true,
    isEdited: isGameOverviewAvailableData,
  });

  const handleAutoSaveConfirm = () => {
    const newValue = getSaveParamsValue(pageKeyCur) || {};

    gameOverview.current?.initialize(newValue);
    setGameOverviewData(newValue);

    setAutoSaveVisible(false);
  };
  const handleAutoSaveCancel = () => {
    setAutoSaveVisible(false);
    deleteSaveParamsByPageKey(pageKeyCur);
  };

  /**
   * 当本地储存有上一次的编辑内容时提示用户是否继续上次的编辑
   */
  useEffect(() => {
    if (showRestorePrompt(pageKeyCur) && userid) {
      setAutoSaveVisible(true);
    }
  }, [userid]);

  // GameOverview模块数据change的处理
  const handleGameOverviewChange = (payload: Record<string, any>) => {
    // if (payload?.comment !== '' && hasChangedRef.current) {
    //   setIsChangeValue(true);
    // }
    hasChangedRef.current = true;
    setGameOverviewData(payload);
  };

  // 新增Product的处理
  const handleSubmitConfirm = async () => {
    const gameOverviewDataTmp = gameOverview.current?.submit();
    if (!gameOverviewDataTmp) return;
    try {
      setSubmitting(true);
      // stage: "Development"
      const params = getParams(gameOverviewDataTmp);

      const { code, msg, data } = await updateProjectInformation(params);
      if (code === 200) {
        handleSubmitSuccess({
          projectId: data,
          projectName: params?.project || '',
        });
        // @ts-ignore
        reportOperationLog(ALL_REPORT_OPERATION_LOG.ADD_PRODUCT_SUBMIT, 'button');
      } else if (code === 30006) {
        messageTips('error', 'Product name is duplicated, please check.');
      } else {
        messageTips('error', msg);
      }
    } catch (exception) {
      // 网络异常提示
      messageTips('error', 'Network error, please check the network and try again later');
    } finally {
      setSubmitting(false);
    }
  };

  // 新增Project成功的处理
  const handleSubmitSuccess = (params?: { projectId?: string; projectName?: string; }) => {
    const { projectId, projectName } = params || {};
    project.current.project_id = projectId || '';
    project.current.project = projectName || '';
    setModalType('success');
    setVisible(true);
    // deleteStoragedProduct();
    setIsSubmit(true);
    deleteSaveParamsByPageKey(pageKeyCur);
  };

  // 跳转到新增Project详情页
  const handleGoToDetail = (e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    setVisible(false);
    setIsSubmit(false);
    deleteSaveParamsByPageKey(pageKeyCur);
    goToProjectDetailsPage({
      projectId: project.current?.project_id || '',
      project: project.current?.project || '',
    });
    notificationTips(
      'success',
      <div className="dbg-add-product-success-tips">
        <p className="dbg-add-product-success-tips-title">Project fields saved!</p>
        <span className="dbg-add-product-success-tips-now">Now complete the details:</span>
        <span className="dbg-add-product-success-tips-des">
          Fill in the remaining sections on the Project Overview page.
        </span>
      </div>,
      5000,
      'dbg-add-pro-success-tips',
    );
  };

  // 取消新增Project
  const handleSubmitCancel = (e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    deleteSaveParamsByPageKey(pageKeyCur);
    history.goBack();
  };

  /**
   * 接着上次存储的Product数据 继续新增 Confirm的处理
   * 用户确认本地存储的Product已新增完成，不用接着存储的Product数据继续新增了，仅关闭弹窗即可
   * 需要删除本地存储的Product数据
   */
  const handleContinueConfirm = () => {
    // deleteStoragedProduct();
    deleteSaveParamsByPageKey(pageKeyCur);
    setVisible(false);
    setIsSubmit(false);
  };

  /**
   * 接着上次存储的Product数据 继续新增 Cancel的处理
   * 用户本地存储的Product数据未新增完成，接着存储的Product数据继续新增
   * 需要从本地存储读取Product数据，回填到Product新增表单
   */
  const handleContinueCancel = () => {
    // if (storagedData) {
    //   gameOverview.current?.initialize(storagedData);
    //   setGameOverviewData(storagedData);
    // }
    setVisible(false);
    setIsSubmit(false);
  };

  // abort、continue两个弹窗模式下，点Confirm的处理
  const handleOthersConfirm = (e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    handleContinueConfirm();
  };

  // abort、continue两个弹窗模式下，点Cancel的处理
  const handleOthersCancel = (e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    deleteSaveParamsByPageKey(pageKeyCur);
    handleContinueCancel();
  };

  return (
    <>
      <Content className="gp-product-add-or-edit oit-layout--fixheader gp-product">
        <Content.Header title="Add New Project" />
        <Content.Body full>
          <GameOverview
            ref={gameOverview}
            onChange={handleGameOverviewChange}
            footer={
              <section className="gp-product-footer gp-product-submit-cancel font14">
                <Button
                  className="gp-product-footer-btn"
                  type="primary"
                  // disabled={!isGameOverviewAvailableData}
                  loading={submitting}
                  onClick={handleSubmitConfirm}
                >
                  Submit
                </Button>
                <Button className="gp-product-footer-btn" type="weak" onClick={handleSubmitCancel}>
                  Cancel
                </Button>
              </section>
            }
          />
        </Content.Body>
      </Content>
      <Modal
        className={classnames('gp-product-submit-or-cancel-modal', {
          'dbg-add-project__add-success-modal': modalType === 'success',
        })}
        size="m"
        visible={visible && modalType !== 'none'}
        onClose={() => {
          if (modalType === 'success') {
            project.current.project_id = '';
            project.current.project = '';
            gameOverview.current?.reset();
            setGameOverviewData(null);
          }
          deleteSaveParamsByPageKey(pageKeyCur);
          setVisible(false);
          setIsSubmit(false);
        }}
      >
        <Modal.Body style={{ overflow: 'visible' }}>
          <section>
            <h5 className="gp-product-submit-or-cancel-modal-title">
              {modalType === 'success' && (
                <Icon
                  className="gp-product-submit-or-cancel-modal-title-icon"
                  type="success"
                  size="l"
                />
              )}
              <span className="gp-product-submit-or-cancel-modal-title-text">
                {modalCfg?.title}
              </span>
            </h5>
            {!!modalCfg?.content?.trim() && (
              <p className="gp-product-submit-or-cancel-modal-info">{modalCfg?.content}</p>
            )}
          </section>
        </Modal.Body>
        <Modal.Footer className="globalpedia-modal-font14">
          {modalType === 'success' ? (
            <Button type="primary" onClick={handleGoToDetail}>
              Go to Detail
            </Button>
          ) : (
            <>
              <Button type="primary" onClick={handleOthersConfirm}>
                Confirm
              </Button>
              <Button type="weak" onClick={handleOthersCancel}>
                Cancel
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
      {/* 离开页面的二次确认弹窗 */}
      <RoutePrompt
        when={!project.current?.project_id && isGameOverviewAvailableData}
        message="Are you sure to cancel the submission？"
        description={`${CONSTANT_TEXTS.CANCELED} information will not be saved`}
        onOk={() => {
          deleteSaveParamsByPageKey(pageKeyCur);
          // deleteStoragedProduct();
        }}
      />

      {/* 咨询自动保存弹窗 */}
      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={handleAutoSaveConfirm}
        onClose={handleAutoSaveCancel}
      />
    </>
  );
};

export default AddNewProduct;
